import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api, { formatBRL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { TrendingUp, Trophy } from "lucide-react";

/**
 * Card no topo do Funil mostrando o progresso do MÊS da vendedora
 * (agregado por todas as vendas dela, independente de curso).
 * Diretoria/Supervisão veem meta = soma dos cursos do mês do escopo (informativo).
 * Iter 42: quando está no /funil e o usuário troca o mês, essa meta segue o
 * mês selecionado (via query param `?mes=YYYY-MM`).
 */
export default function MinhaMetaMesCard() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Só respeita o mês da URL quando estamos no funil
  const isFunil = location.pathname.startsWith("/funil");
  const mesParam = isFunil ? searchParams.get("mes") : null;

  useEffect(() => {
    const q = mesParam ? `?mes_ano=${mesParam}` : "";
    const load = () => api.get(`/dashboards/meta-usuario${q}`).then((r) => setMeta(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [mesParam]);

  if (!meta || !meta.meta) return null;
  const pct = Math.min(100, meta.percentual);
  const bateu = meta.realizado >= meta.meta;

  const nomes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const [y, m] = (meta.mes_ano || "").split("-");
  const mesLabel = m ? `${nomes[Number(m) - 1]}/${y}` : "";

  const label = user?.role === "vendedora" ? "Minha meta do mês" : "Meta do mês (escopo)";

  return (
    <Card
      className={`p-4 rounded-2xl border-2 ${bateu ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-white" : "border-blue-300 bg-gradient-to-br from-blue-50 to-white"}`}
      data-testid="minha-meta-mes-card"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bateu ? "bg-emerald-500 text-white" : "bg-blue-500 text-white"}`}>
          {bateu ? <Trophy className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] uppercase font-bold tracking-widest text-slate-500 flex items-center gap-2">
            {label}
            <span className="normal-case tracking-normal text-slate-400 font-normal">· {mesLabel}</span>
          </div>
          <div className="font-display font-bold text-slate-900 leading-tight">
            <span className={`tabular-nums text-2xl ${bateu ? "text-emerald-600" : "text-slate-900"}`} data-testid="mmm-realizado">
              {formatBRL(meta.realizado)}
            </span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="tabular-nums text-base text-slate-700">{formatBRL(meta.meta)}</span>
            <span className="text-slate-500 text-sm ml-2">
              {bateu ? "· META BATIDA! 🎯" : `· faltam ${formatBRL(meta.falta)}`}
            </span>
          </div>
          <div className="progress-track h-2 mt-2">
            <div
              className={`progress-fill ${bateu ? "win" : pct < 40 ? "warn" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {user?.role === "vendedora" ? (
              <>Vendas suas em TODOS os cursos do mês · {pct.toFixed(0)}% da meta</>
            ) : (
              <>Somatório do escopo · {pct.toFixed(0)}%</>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
